import React, { useMemo } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

const fmtC = (n) =>
  n == null || Number.isNaN(Number(n))
    ? '$0.00'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Refund-checkbox UX for the Record Payment drawer.
 *
 * Triggers a top banner + bottom checkbox in two cases:
 *   1. paid_by ≠ "BillPortal"  → suggest refunding the entire paid amount back to whoever paid.
 *   2. paid_amount < requested → suggest refunding the *shortfall* back to the customer/anchor.
 *
 * When the checkbox is on, a small subsection opens showing:
 *   • Refund amount (editable, defaults per the trigger reason)
 *   • Refund TO  (defaults to the paid_by name; user cannot easily change to avoid mistakes)
 *   • Notes
 *
 * The parent owns the state (controlled). Submit the form normally — the
 * parent then calls /record-payment AND /record-refund sequentially when
 * `value.enabled` is true.
 *
 * Props
 *   paidByName        — current "Paid by" name on the parent form
 *   paidAmount        — current paid amount entered on the parent form
 *   requestedAmount   — bill's requested amount (from the bill record)
 *   value             — { enabled, refund_amount, refund_to, notes }
 *   onChange          — (next) => void
 *   orgName           — current org name (used to default Refund TO when paid_by is "Customer" etc.)
 *   readOnly          — disable all controls (e.g. in locked/view mode)
 */
export const RefundCheckboxSection = ({
  paidByName,
  paidAmount,
  requestedAmount,
  value,
  onChange,
  readOnly = false,
}) => {
  const paid = parseFloat(paidAmount || 0) || 0;
  const requested = parseFloat(requestedAmount || 0) || 0;

  const trigger = useMemo(() => {
    const pb = (paidByName || '').trim();
    const isBillPortal = !pb || /billportal/i.test(pb);
    const hasShortfall = paid > 0 && requested > 0 && paid + 0.01 < requested;
    const shortfall = hasShortfall ? Math.round((requested - paid) * 100) / 100 : 0;

    // Case A — Paid by BillPortal AND shortfall vs requested:
    // BillPortal collects on behalf of the org and the vendor was paid less than
    // what was pulled from the customer — refund the remaining back to the customer.
    if (isBillPortal && hasShortfall) {
      return {
        kind: 'billportal_shortfall',
        suggestedAmount: shortfall,
        suggestedTo: 'Customer',
        message: `Paid ${fmtC(paid)} of ${fmtC(requested)} requested. Refund the remaining ${fmtC(shortfall)} to the Customer.`,
      };
    }

    // Case B — Paid by Partner (Anchor) or Customer (anyone but BillPortal):
    // the funds came from someone other than BillPortal, so we typically need to
    // refund that party back. Default the refund recipient to the payer.
    if (!isBillPortal && paid > 0) {
      return {
        kind: 'paid_by_other',
        suggestedAmount: paid,
        suggestedTo: pb,
        message: `Paid by ${pb}. You may need to refund ${fmtC(paid)} back to ${pb}. Tick the checkbox below to record the refund without leaving this drawer.`,
      };
    }
    return null;
  }, [paidByName, paid, requested]);

  // When the trigger changes, keep the suggested amount + recipient in sync,
  // but only if the user hasn't already overridden them (we track this with
  // a `_user_edited_amount` flag the parent doesn't have to worry about —
  // we just respect whatever's currently in value).
  React.useEffect(() => {
    if (!trigger) return;
    // Only auto-fill when the section transitions from "no suggestion" to
    // "suggestion" — i.e. fields are still empty.
    if (value?.enabled) return;
    if ((value?.refund_amount == null || value?.refund_amount === '') && (value?.refund_to == null || value?.refund_to === '')) {
      onChange({
        ...(value || {}),
        refund_amount: trigger.suggestedAmount,
        refund_to: trigger.suggestedTo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger?.kind, trigger?.suggestedAmount, trigger?.suggestedTo]);

  if (!trigger) return null;

  const set = (patch) => onChange({ ...(value || { enabled: false }), ...patch });

  return (
    <div className="space-y-2.5" data-testid="refund-checkbox-section">
      {/* Top banner */}
      <div
        className="rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-[12px] leading-[16px] text-amber-800 flex items-start gap-2"
        data-testid="refund-checkbox-banner"
      >
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>{trigger.message}</span>
      </div>

      {/* Checkbox */}
      <label className="flex items-center gap-2 text-xs select-none cursor-pointer">
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
          checked={!!value?.enabled}
          disabled={readOnly}
          onChange={(e) => set({ enabled: e.target.checked })}
          data-testid="refund-checkbox-toggle"
        />
        <span className="font-medium text-foreground inline-flex items-center gap-1">
          <RotateCcw className="h-3 w-3 text-emerald-600" /> Make a refund for this
        </span>
      </label>

      {/* Refund subsection */}
      {value?.enabled && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="refund-checkbox-fields">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Refund amount</label>
              <input
                type="number"
                step="0.01"
                value={value.refund_amount ?? ''}
                onChange={(e) => set({ refund_amount: e.target.value })}
                disabled={readOnly}
                className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs font-mono"
                data-testid="refund-checkbox-amount"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Refund to</label>
              <input
                type="text"
                value={value.refund_to ?? ''}
                onChange={(e) => set({ refund_to: e.target.value })}
                disabled={readOnly}
                className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs"
                data-testid="refund-checkbox-to"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1">Refund notes</label>
            <textarea
              rows={2}
              value={value.notes ?? ''}
              onChange={(e) => set({ notes: e.target.value })}
              disabled={readOnly}
              placeholder="Why this refund is being recorded…"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs resize-y"
              data-testid="refund-checkbox-notes"
            />
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            On submit, this entry is recorded against the bill and shown in <strong className="not-italic">Org &rsaquo; Payments &rsaquo; Refunds Report</strong> just like a refund entered from the Refunds drawer.
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Builder for the parent's POST body to `/files/{pf_id}/record-refund`.
 * Returns null if the section is disabled or required fields are missing.
 */
export const buildRefundPayload = ({ value, accountId, utilityId, userId, userName }) => {
  if (!value?.enabled) return null;
  const amt = parseFloat(value.refund_amount || 0);
  if (!amt || amt <= 0) return null;
  return {
    account_id: accountId,
    utility_id: utilityId,
    refund_amount: amt,
    refunded_by: value.refund_to || '3rd Party Payment',
    refund_to: value.refund_to || '',
    refund_method: 'ach',
    refund_date: new Date().toISOString().split('T')[0],
    refund_reference: '',
    refund_completed: true,
    refund_notes: value.notes || '',
    user_id: userId,
    user_name: userName,
    created_by: userId,
    created_by_name: userName,
  };
};

export default RefundCheckboxSection;
